import { mutationOptions, queryOptions } from "@tanstack/react-query";
import { rbacKeys } from "@/constant/queryKeys";
import { apiRequestV2, queryClient } from "@/lib/queryClient";
import type { OuraListParams } from "@/types/oura";
import type {
  RbacBulkWhitelistResult,
  RbacManagedPartnersResponse,
  RbacPartnerAdminsResponse,
  RbacPermissionsResponse,
  RbacRolePermissionsResponse,
  RbacRolesResponse,
  RbacUserRolesResponse,
  RbacUsersResponse,
  RbacWhitelistResponse,
} from "@/types/rbac";

function queryString(params?: Record<string, unknown>) {
  const search = new URLSearchParams();
  Object.entries(params ?? {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value) !== "") {
      search.set(key, String(value));
    }
  });
  const value = search.toString();
  return value ? `?${value}` : "";
}

export function invalidateRbac() {
  queryClient.invalidateQueries({ queryKey: rbacKeys.all });
}

export function getRbacUsersOptions(params: OuraListParams) {
  return queryOptions({
    queryKey: rbacKeys.users(params),
    queryFn: () => apiRequestV2<RbacUsersResponse>("GET", `/api/v1/rbac/users${queryString(params)}`),
  });
}

export function getRbacRolesOptions() {
  return queryOptions({
    queryKey: rbacKeys.roles,
    queryFn: () => apiRequestV2<RbacRolesResponse>("GET", "/api/v1/rbac/roles"),
  });
}

export function getRbacPermissionsOptions() {
  return queryOptions({
    queryKey: rbacKeys.permissions,
    queryFn: () => apiRequestV2<RbacPermissionsResponse>("GET", "/api/v1/rbac/permissions"),
  });
}

export function getRbacUserRolesOptions(userId?: number | null) {
  return queryOptions({
    queryKey: rbacKeys.userRoles(userId),
    queryFn: () => apiRequestV2<RbacUserRolesResponse>("GET", `/api/v1/rbac/users/${userId}/roles`),
    enabled: Number.isFinite(userId),
  });
}

export function getRbacUserPartnersOptions(userId?: number | null) {
  return queryOptions({
    queryKey: rbacKeys.userPartners(userId),
    queryFn: () => apiRequestV2<RbacManagedPartnersResponse>("GET", `/api/v1/rbac/users/${userId}/managed-partners`),
    enabled: Number.isFinite(userId),
  });
}

export function getRbacRolePermissionsOptions(roleName?: string | null) {
  return queryOptions({
    queryKey: rbacKeys.rolePermissions(roleName),
    queryFn: () =>
      apiRequestV2<RbacRolePermissionsResponse>(
        "GET",
        `/api/v1/rbac/roles/${encodeURIComponent(roleName ?? "")}/permissions`,
      ),
    enabled: Boolean(roleName),
  });
}

export function getRbacPartnerAdminsOptions(partnerId?: number | null) {
  return queryOptions({
    queryKey: rbacKeys.partnerAdmins(partnerId),
    queryFn: () => apiRequestV2<RbacPartnerAdminsResponse>("GET", `/api/v1/rbac/partners/${partnerId}/admins`),
    enabled: Number.isFinite(partnerId),
  });
}

export function getRbacWhitelistOptions() {
  return queryOptions({
    queryKey: rbacKeys.whitelist,
    queryFn: () => apiRequestV2<RbacWhitelistResponse>("GET", "/api/v1/rbac/whitelist"),
  });
}

export function rbacMutationOptions<TPayload, TResult = unknown>(method: string, url: string) {
  return mutationOptions<TResult, Error, TPayload>({
    mutationFn: (data) => apiRequestV2<TResult>(method, url, data),
    onSuccess: invalidateRbac,
  });
}

export function clearRbacCacheOptions() {
  return mutationOptions({
    mutationFn: () => apiRequestV2("POST", "/api/v1/rbac/cache/clear"),
  });
}

export function createBulkWhitelistOptions() {
  return mutationOptions<RbacBulkWhitelistResult, Error, { entries: Array<{ identifier: string; assignedRoleName: string }> }>({
    mutationFn: (data) => apiRequestV2<RbacBulkWhitelistResult>("POST", "/api/v1/rbac/whitelist", data),
    onSuccess: invalidateRbac,
  });
}
